import { useState } from "react";
import { TextField, TextFieldProps } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Autocomplete } from "@mui/lab";
import { FormikProps } from "formik";
import { startCase } from "lodash";

import { IdsExpandMore as ArrowDropDownIcon } from "components/shared/svgIcons";

import { IForm, IFormField } from "types/form";

const useStyles = makeStyles({
  autoCompleteInputRoot: {
    paddingLeft: "12px !important",
  },
  autoCompletePopupIcon: {
    width: "1.125em",
    height: "1.125em",
    top: "calc(50% - 14px)",
  },
});

const FormikFormField = <T extends IForm>(
  props: IFormField<T> & FormikProps<T> & { textFieldProps?: TextFieldProps }
) => {
  const {
    name,
    label,
    type,
    required,
    values,
    touched,
    errors,
    handleBlur,
    handleChange,
    textFieldProps = {},
  } = props;

  return (
    <TextField
      name={name}
      label={label ?? startCase(name)}
      value={values[name]}
      type={type ?? "text"}
      fullWidth
      required={required ?? true}
      variant="filled"
      size="small"
      error={errors[name] && touched[name] ? true : false}
      onChange={handleChange}
      onBlur={handleBlur}
      {...textFieldProps}
      helperText={
        errors[name] && touched[name]
          ? errors[name]
          : textFieldProps.helperText ?? ""
      }
    />
  );
};

export const FormikAutoComplete = <T extends IForm, Option>(
  props: IFormField<T> &
    FormikProps<T> & {
      options: Option[];
      textFieldName: keyof Option;
      valueFieldName: keyof Option;
    }
) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const {
    name,
    label,
    required,
    values,
    setFieldValue,
    touched,
    errors,
    handleBlur,
    options,
    textFieldName,
    valueFieldName,
  } = props;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option) => option[textFieldName] as any}
      onChange={(e, value) => {
        setFieldValue(name, value ? value[valueFieldName] : "");
        if (name === "country") {
          setFieldValue("state", "", true);
        }
      }}
      onOpen={(e) => {
        handleBlur(e);
        setOpen(true);
      }}
      onClose={() => setOpen(false)}
      includeInputInList
      classes={{
        inputRoot: classes.autoCompleteInputRoot,
      }}
      disableClearable
      open={open}
      popupIcon={
        <ArrowDropDownIcon
          color={errors[name] && touched[name] ? "error" : "primary"}
          className={classes.autoCompletePopupIcon}
        />
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password",
            ...(values[name] || open ? {} : { value: "" }),
          }}
          name={name}
          label={label ?? startCase(name)}
          fullWidth
          required={required ?? true}
          variant="filled"
          size="small"
          error={errors[name] && touched[name] ? true : false}
          onBlur={handleBlur}
          helperText={errors[name] && touched[name] ? errors[name] : ""}
        />
      )}
    />
  );
};

export default FormikFormField;
